import React, { useState, useEffect } from "react";
import { collection, doc, setDoc, serverTimestamp, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { THEME_COLOR } from "../../config/club";

export default function JoinerCreateMatchRequest({ clubId, sessionId, uid, queue }) {
  const [selected, setSelected] = useState([]);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // Collapsible State

  useEffect(() => {
    if (!uid || !clubId || !sessionId) return;
    const q = query(
      collection(db, "clubs", clubId, "sessions", sessionId, "matchRequests"),
      where("players", "array-contains", uid),
      where("status", "==", "pending")
    );
    return onSnapshot(q, (snap) => setHasPendingRequest(!snap.empty));
  }, [uid, clubId, sessionId]);

  const togglePlayer = (p) => {
    if (selected.find(x => x.id === p.id)) {
      setSelected(selected.filter(x => x.id !== p.id));
      return;
    }
    if (selected.length >= 3) return;
    setSelected([...selected, p]);
  };

  const createRequest = async () => {
    if (selected.length !== 3 || hasPendingRequest) return;
    const teamA = [uid, selected[0].id];
    const teamB = [selected[1].id, selected[2].id];
    const ref = doc(collection(db, "clubs", clubId, "sessions", sessionId, "matchRequests"));
    
    await setDoc(ref, {
      captain: uid,
      players: [uid, ...selected.map(s => s.id)],
      teamA, teamB,
      accepted: [uid],
      declined: [],
      status: "pending",
      createdAt: serverTimestamp()
    });
    setSelected([]);
    setIsExpanded(false);
  };

  const partner = selected[0];
  const opp1 = selected[1];
  const opp2 = selected[2];

  return (
    <div className="mt-4 px-2 card border-0 shadow-sm rounded-4 mb-4 bg-body-tertiary overflow-hidden transition-all">
      {/* HEADER / TOGGLE AREA */}
      <div 
        className="card-header bg-transparent border-0 p-3 d-flex justify-content-between align-items-center" 
        style={{ cursor: 'pointer' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="d-flex align-items-center gap-2">
          <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white" style={{ width: '28px', height: '28px', backgroundColor: THEME_COLOR }}>
            <i className="bi bi-plus-lg"></i>
          </div>
          <div>
            <h6 className="fw-black text-uppercase mb-0 text-body" style={{ fontSize: '0.8rem', letterSpacing: '0.5px' }}>
              Request Match
            </h6>
            {!isExpanded && selected.length > 0 && (
              <div className="text-muted fw-bold" style={{ fontSize: '0.65rem' }}>
                {selected.length}/3 players selected
              </div>
            )}
          </div>
        </div>
        <i className={`bi bi-chevron-down transition-all ${isExpanded ? 'rotate-180' : ''}`}></i>
      </div>

      {/* COLLAPSIBLE CONTENT */}
      <div className={`collapse-content ${isExpanded ? 'expanded' : ''}`}>
        <div className="card-body p-3 pt-0">
          {/* TEAM BUILDER VISUAL */}
          <div className="row g-2 mb-3">
            <div className="col-6">
              <div className="p-2 rounded-4 border border-dashed border-secondary-subtle text-center h-100 bg-body">
                <div className="fw-bold text-muted text-uppercase mb-2" style={{ fontSize: '0.6rem' }}>Your Team</div>
                <div className="small fw-bold mb-1 text-body">You</div>
                <div className={`small rounded-3 py-1 px-2 ${partner ? 'text-white shadow-sm' : 'text-muted opacity-50'}`} 
                     style={{ transition: 'all 0.3s ease', backgroundColor: partner ? THEME_COLOR : 'rgba(128,128,128,0.05)', minHeight: '26px' }}>
                  {partner ? partner.name : "+ Partner"}
                </div>
              </div>
            </div>

            <div className="col-6">
              <div className="p-2 rounded-4 border border-dashed border-secondary-subtle text-center h-100 bg-body">
                <div className="fw-bold text-muted text-uppercase mb-2" style={{ fontSize: '0.6rem' }}>Opponents</div>
                <div className={`small rounded-3 py-1 px-2 mb-1 ${opp1 ? 'bg-secondary text-white' : 'text-muted opacity-50'}`} 
                     style={{ transition: 'all 0.3s ease', minHeight: '26px', backgroundColor: opp1 ? 'var(--bs-secondary)' : 'rgba(128,128,128,0.05)' }}>
                  {opp1 ? opp1.name : "+ Opp 1"}
                </div>
                <div className={`small rounded-3 py-1 px-2 ${opp2 ? 'bg-secondary text-white' : 'text-muted opacity-50'}`} 
                     style={{ transition: 'all 0.3s ease', minHeight: '26px', backgroundColor: opp2 ? 'var(--bs-secondary)' : 'rgba(128,128,128,0.05)' }}>
                  {opp2 ? opp2.name : "+ Opp 2"}
                </div>
              </div>
            </div>
          </div>

          {/* PLAYER SELECTOR */}
          <div className="mb-3">
            <div className="fw-bold text-muted text-uppercase mb-2 px-1" style={{ fontSize: '0.6rem' }}>In Queue</div>
            <div className="d-flex flex-wrap gap-2">
              {queue.filter(p => p.id !== uid).map(p => {
                const isSelected = selected.find(x => x.id === p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => togglePlayer(p)}
                    disabled={hasPendingRequest}
                    className={`btn btn-sm rounded-pill px-3 border-0 ${isSelected ? "text-white shadow-sm" : "bg-secondary-subtle text-body"}`}
                    style={{ fontSize: '0.75rem', fontWeight: '600', backgroundColor: isSelected ? THEME_COLOR : '', opacity: hasPendingRequest ? 0.5 : 1 }}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* WARNING & ACTIONS */}
          {hasPendingRequest ? (
            <div className="alert alert-warning border-0 rounded-4 py-2 px-3 small mb-0 d-flex align-items-center gap-2">
               <i className="bi bi-exclamation-circle-fill text-warning"></i>
               <span className="text-body-secondary">Finish your pending match first.</span>
            </div>
          ) : (
            <button
              className="btn btn-lg w-100 fw-bold text-white rounded-4 border-0 shadow-sm"
              style={{ backgroundColor: THEME_COLOR, fontSize: '0.9rem', opacity: selected.length === 3 ? 1 : 0.4 }}
              disabled={selected.length !== 3}
              onClick={createRequest}
            >
              {selected.length === 3 ? "🚀 Send Challenges" : `Pick ${3 - selected.length} more`}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .fw-black { font-weight: 900; }
        .transition-all { transition: all 0.3s ease; }
        .rotate-180 { transform: rotate(180deg); }
        .border-dashed { border-style: dashed !important; border-width: 1.5px !important; }
        
        .collapse-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.4s ease-out, opacity 0.3s ease;
          opacity: 0;
        }
        
        .collapse-content.expanded {
          max-height: 500px; /* Adjust as needed */
          opacity: 1;
        }
      `}</style>
    </div>
  );
}